import { useMemo, useState } from "react";
import { Box, Stack, Typography, FormControlLabel, Checkbox } from "@mui/material";
import { groupByClass } from "../../utils/timetableUtils";
import ClassCard from "../cards/ClassCard";

export default function MorningLane({
  residents = [],
  classList = [],
  selectedDate,
  selectedCampusId,
  onOpenClasses,
  tv = false,
  tvColumns = 3,
  tvCardRange = null,
}) {
  const [showAdults, setShowAdults] = useState(false);

  // Names of staff who teach a class this slot — from the `teacherName` meta on
  // student rows AND the class list's `leaderName` (which also covers EMPTY
  // classes, whose teacher has no student rows). Their own adult row has no
  // className, so without this they land in "Unassigned" even though they're
  // shown as the class's Teacher header.
  const teacherNames = useMemo(() => {
    const set = new Set();
    residents.forEach((p) => {
      if (p?.teacherName) set.add(String(p.teacherName).trim().toLowerCase());
    });
    (classList || []).forEach((c) => {
      if (c?.leaderName) set.add(String(c.leaderName).trim().toLowerCase());
    });
    return set;
  }, [residents, classList]);

  const visibleResidents = useMemo(() => {
    const base = showAdults
      ? residents
      : residents.filter((person) => person.personType !== "ADULT");
    // A teacher is already represented by their class's Teacher header — don't
    // also list them as an unassigned adult.
    return base.filter((person) => {
      if (person.personType !== "ADULT") return true;
      if (person.className) return true; // already placed in a class
      return !teacherNames.has(String(person.personName || "").trim().toLowerCase());
    });
  }, [residents, showAdults, teacherNames]);

  const grouped = useMemo(() => groupByClass(visibleResidents), [visibleResidents]);

  // Teacher per class from the class list, so an empty class (no people to carry
  // teacherName meta) can still show its teacher. Keyed by normalised class name.
  const classMeta = useMemo(() => {
    const map = new Map();
    (classList || []).forEach((c) => {
      const name = String(c?.groupName || "").trim();
      if (name) map.set(name.toLowerCase(), { name, teacher: c?.leaderName || "" });
    });
    return map;
  }, [classList]);

  // Union of classes that have people AND empty classes from the class list, so
  // scheduled-but-empty classes still appear. Joined by class name (the only key
  // shared between the two datasets).
  const orderedGroups = useMemo(() => {
    const byNorm = new Map();
    Object.entries(grouped).forEach(([className, people]) => {
      byNorm.set(String(className || "").trim().toLowerCase(), { name: className, people });
    });
    classMeta.forEach((meta, norm) => {
      if (norm === "unassigned") return;
      if (!byNorm.has(norm)) byNorm.set(norm, { name: meta.name, people: [] });
    });
    return Array.from(byNorm.values()).sort((a, b) => {
      const aUnassigned = String(a.name || "").trim().toLowerCase() === "unassigned";
      const bUnassigned = String(b.name || "").trim().toLowerCase() === "unassigned";
      if (aUnassigned && !bUnassigned) return -1;
      if (!aUnassigned && bUnassigned) return 1;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [grouped, classMeta]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Morning</Typography>

        {tv ? null : (
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={showAdults}
                onChange={(e) => setShowAdults(e.target.checked)}
                size="small"
              />
            }
            label="Show adults"
            sx={{ mr: 1 }}
          />

          <Typography
            variant="body2"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => onOpenClasses?.(selectedDate, selectedCampusId)}
          >
            Open Classes →
          </Typography>
        </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {(tvCardRange ? orderedGroups.slice(tvCardRange[0], tvCardRange[1]) : orderedGroups).map((cls, i) => {
          const index = i + (tvCardRange ? tvCardRange[0] : 0);
          const meta = classMeta.get(String(cls.name || "").trim().toLowerCase());
          return (
            <ClassCard
              key={cls.name || `class-${index}`}
              className={cls.name}
              residents={cls.people}
              teacherFallback={meta?.teacher || ""}
              colorIndex={index}
              tv={tv}
              tvColumns={tvColumns}
            />
          );
        })}
      </Stack>
    </Box>
  );
}